import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';
import { CloudSun, Loader2, ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, BarChart3 } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const WEATHER_ICONS = {
  0: { icon: '☀️', color: 'text-yellow-500' },
  1: { icon: '🌤️', color: 'text-yellow-400' },
  2: { icon: '⛅', color: 'text-gray-400' },
  3: { icon: '☁️', color: 'text-gray-500' },
  45: { icon: '🌫️', color: 'text-gray-400' },
  48: { icon: '🌫️', color: 'text-blue-300' },
  51: { icon: '🌦️', color: 'text-blue-300' },
  53: { icon: '🌦️', color: 'text-blue-400' },
  55: { icon: '🌦️', color: 'text-blue-500' },
  56: { icon: '🌧️', color: 'text-blue-300' },
  57: { icon: '🌧️', color: 'text-blue-400' },
  61: { icon: '🌧️', color: 'text-blue-400' },
  63: { icon: '🌧️', color: 'text-blue-500' },
  65: { icon: '🌧️', color: 'text-blue-600' },
  66: { icon: '🌧️', color: 'text-indigo-400' },
  67: { icon: '🌧️', color: 'text-indigo-500' },
  71: { icon: '🌨️', color: 'text-blue-200' },
  73: { icon: '🌨️', color: 'text-blue-300' },
  75: { icon: '❄️', color: 'text-blue-400' },
  77: { icon: '❄️', color: 'text-blue-300' },
  80: { icon: '🌦️', color: 'text-blue-400' },
  81: { icon: '🌧️', color: 'text-blue-500' },
  82: { icon: '🌧️', color: 'text-blue-600' },
  85: { icon: '🌨️', color: 'text-blue-300' },
  86: { icon: '🌨️', color: 'text-blue-400' },
  95: { icon: '⛈️', color: 'text-purple-500' },
  96: { icon: '⛈️', color: 'text-purple-600' },
  99: { icon: '⛈️', color: 'text-purple-700' },
};

function getWeatherStyle(code) {
  return WEATHER_ICONS[code] || { icon: '❓', color: 'text-gray-400' };
}

function tempColor(temp) {
  if (temp === null || temp === undefined) return '';
  if (temp <= 0) return 'text-blue-500 font-semibold';
  if (temp <= 10) return 'text-sky-500';
  if (temp <= 20) return 'text-green-500';
  if (temp <= 30) return 'text-orange-500';
  return 'text-red-500 font-semibold';
}

export function MeteoPage() {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('asc');
  const [tab, setTab] = useState('meteo');
  const [analyseData, setAnalyseData] = useState(null);
  const [analyseLoading, setAnalyseLoading] = useState(false);

  // Default: current month
  const now = new Date();
  const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const lastDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(lastDay);

  const fetchData = async () => {
    if (!dateFrom || !dateTo) { toast.error('Sélectionnez les dates'); return; }
    setLoading(true);
    try {
      const res = await api.get(`/meteo?date_from=${dateFrom}&date_to=${dateTo}`);
      setData(res.data);
    } catch (err) {
      toast.error('Erreur chargement météo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchAnalyse = async () => {
    if (!dateFrom || !dateTo) return;
    setAnalyseLoading(true);
    try {
      const res = await api.get(`/analyse-ca-meteo?date_from=${dateFrom}&date_to=${dateTo}`);
      setAnalyseData(res.data);
    } catch { toast.error('Erreur chargement analyse'); }
    finally { setAnalyseLoading(false); }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const sortedData = [...data].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'date') cmp = a.date.localeCompare(b.date);
    else if (sortField === 'temp_max') cmp = (a.temp_max ?? -999) - (b.temp_max ?? -999);
    else if (sortField === 'temp_min') cmp = (a.temp_min ?? -999) - (b.temp_min ?? -999);
    else if (sortField === 'weather') cmp = (a.weather_desc || '').localeCompare(b.weather_desc || '');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Stats
  const avgMax = data.length ? (data.reduce((s, d) => s + (d.temp_max ?? 0), 0) / data.length).toFixed(1) : '-';
  const avgMin = data.length ? (data.reduce((s, d) => s + (d.temp_min ?? 0), 0) / data.length).toFixed(1) : '-';
  const rainyDays = data.filter(d => [51,53,55,61,63,65,66,67,80,81,82].includes(d.weather_code)).length;
  const sunnyDays = data.filter(d => [0,1].includes(d.weather_code)).length;

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title">Météo</h1>
          <p className="text-muted-foreground mt-1">Historique et prévisions - Gif-sur-Yvette</p>
        </div>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">Du</Label>
              <Input type="date" className="w-[160px] h-9" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="meteo-date-from" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">Au</Label>
              <Input type="date" className="w-[160px] h-9" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="meteo-date-to" />
            </div>
            <Button onClick={() => { fetchData(); fetchAnalyse(); }} disabled={loading || analyseLoading} className="gap-2" data-testid="meteo-search-btn">
              {(loading || analyseLoading) ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudSun className="w-4 h-4" />}
              Rechercher
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="meteo" className="gap-2"><CloudSun className="w-4 h-4" /> Météo</TabsTrigger>
          <TabsTrigger value="analyse" className="gap-2" data-testid="tab-analyse-ca"><BarChart3 className="w-4 h-4" /> Analyse CA vs Météo</TabsTrigger>
        </TabsList>

        <TabsContent value="meteo" className="space-y-6">
      {/* Stats cards */}
      {data.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">Temp. moy. max</p>
              <p className="text-2xl font-bold text-orange-500">{avgMax}°C</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">Temp. moy. min</p>
              <p className="text-2xl font-bold text-sky-500">{avgMin}°C</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">Jours ensoleillés</p>
              <p className="text-2xl font-bold text-yellow-500">{sunnyDays}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">Jours de pluie</p>
              <p className="text-2xl font-bold text-blue-500">{rainyDays}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('date')} data-testid="meteo-sort-date">
                  <span className="flex items-center">Date<SortIcon field="date" /></span>
                </TableHead>
                <TableHead className="text-center cursor-pointer select-none" onClick={() => handleSort('temp_min')} data-testid="meteo-sort-temp-min">
                  <span className="flex items-center justify-center">Temp. Min<SortIcon field="temp_min" /></span>
                </TableHead>
                <TableHead className="text-center cursor-pointer select-none" onClick={() => handleSort('temp_max')} data-testid="meteo-sort-temp-max">
                  <span className="flex items-center justify-center">Temp. Max<SortIcon field="temp_max" /></span>
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('weather')} data-testid="meteo-sort-weather">
                  <span className="flex items-center">Météo<SortIcon field="weather" /></span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                    Aucune donnée météo
                  </TableCell>
                </TableRow>
              ) : sortedData.map(row => {
                const ws = getWeatherStyle(row.weather_code);
                return (
                  <TableRow key={row.date} data-testid={`meteo-row-${row.date}`}>
                    <TableCell className="font-medium">{formatDate(row.date)}</TableCell>
                    <TableCell className={`text-center ${tempColor(row.temp_min)}`}>
                      {row.temp_min !== null ? `${row.temp_min.toFixed(1)}°C` : '-'}
                    </TableCell>
                    <TableCell className={`text-center ${tempColor(row.temp_max)}`}>
                      {row.temp_max !== null ? `${row.temp_max.toFixed(1)}°C` : '-'}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <span className="text-xl">{ws.icon}</span>
                        <span className={ws.color}>{row.weather_desc}</span>
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </TabsContent>

        {/* ====== TAB ANALYSE CA VS METEO ====== */}
        <TabsContent value="analyse" className="space-y-6">
          {analyseLoading ? (
            <Card><CardContent className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></CardContent></Card>
          ) : !analyseData ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">Cliquez sur "Rechercher" pour charger l'analyse</CardContent></Card>
          ) : (
            <>
              {/* Stats par type de météo */}
              {Object.keys(analyseData.stats_by_weather).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {Object.entries(analyseData.stats_by_weather).sort((a, b) => b[1].avg_ca - a[1].avg_ca).map(([grp, stats]) => {
                    const icons = { 'Ensoleillé': '☀️', 'Nuageux': '☁️', 'Pluie': '🌧️', 'Neige': '❄️', 'Orage': '⛈️' };
                    return (
                      <Card key={grp}>
                        <CardContent className="p-4 text-center">
                          <p className="text-2xl mb-1">{icons[grp] || '🌡️'}</p>
                          <p className="text-xs text-muted-foreground font-medium">{grp}</p>
                          <p className="text-xl font-bold mt-1">{formatCurrency(stats.avg_ca)}</p>
                          <p className="text-xs text-muted-foreground">CA moyen/jour ({stats.count}j)</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Graphique CA + Température */}
              {analyseData.daily.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">CA et Température</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={analyseData.daily.filter(d => d.ca > 0 || d.temp_max !== null)}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="date" tickFormatter={d => { const p = d.split('-'); return `${p[2]}/${p[1]}`; }} fontSize={11} />
                        <YAxis yAxisId="ca" orientation="left" tickFormatter={v => `${v}€`} fontSize={11} />
                        <YAxis yAxisId="temp" orientation="right" tickFormatter={v => `${v}°`} fontSize={11} />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === 'CA') return [`${value.toFixed(2)}€`, 'CA'];
                            return [`${value?.toFixed(1)}°C`, name];
                          }}
                          labelFormatter={d => formatDate(d)}
                        />
                        <Legend />
                        <Line yAxisId="ca" type="monotone" dataKey="ca" name="CA" stroke="#10b981" strokeWidth={2} dot={false} />
                        <Line yAxisId="temp" type="monotone" dataKey="temp_max" name="Temp. Max" stroke="#f97316" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                        <Line yAxisId="temp" type="monotone" dataKey="temp_min" name="Temp. Min" stroke="#3b82f6" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Graphique CA moyen par type de météo */}
              {Object.keys(analyseData.stats_by_weather).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-base">CA moyen par type de météo</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={Object.entries(analyseData.stats_by_weather).map(([grp, s]) => ({ name: grp, ca_moyen: s.avg_ca, jours: s.count }))}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="name" fontSize={12} />
                        <YAxis tickFormatter={v => `${v}€`} fontSize={11} />
                        <Tooltip formatter={(v) => [`${v.toFixed(2)}€`, 'CA moyen']} />
                        <Bar dataKey="ca_moyen" name="CA moyen" radius={[6, 6, 0, 0]}>
                          {Object.entries(analyseData.stats_by_weather).map(([grp], i) => {
                            const colors = { 'Ensoleillé': '#f59e0b', 'Nuageux': '#94a3b8', 'Pluie': '#3b82f6', 'Neige': '#a5b4fc', 'Orage': '#8b5cf6' };
                            return <Cell key={i} fill={colors[grp] || '#6b7280'} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Tableau détaillé */}
              <Card>
                <CardHeader><CardTitle className="text-base">Détail journalier</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">CA</TableHead>
                        <TableHead className="text-center">Temp.</TableHead>
                        <TableHead>Météo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analyseData.daily.filter(d => d.ca > 0).map(row => {
                        const ws = getWeatherStyle(row.weather_code);
                        return (
                          <TableRow key={row.date}>
                            <TableCell className="font-medium">{formatDate(row.date)}</TableCell>
                            <TableCell className="text-right font-semibold text-green-600">{formatCurrency(row.ca)}</TableCell>
                            <TableCell className="text-center text-sm">
                              {row.temp_min !== null ? <span className={tempColor(row.temp_min)}>{row.temp_min.toFixed(0)}°</span> : '-'}
                              {' / '}
                              {row.temp_max !== null ? <span className={tempColor(row.temp_max)}>{row.temp_max.toFixed(0)}°</span> : '-'}
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1">
                                <span>{ws.icon}</span>
                                <span className={`text-sm ${ws.color}`}>{row.weather_desc}</span>
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
